import * as React from "react";
import {
    foBoLabels,
    hashColor,
    type FoBoTeam,
    type FoBoUser,
} from "./foBoLogic";

/**
 * Geteilte „FO/BO"-Verantwortlichkeits-Darstellung (SP6). Aus der
 * `ContactTableRedesign`-Zelle „K-Verantwortlicher" extrahiert, damit die
 * Vorgang-Detail-Personenzeile denselben Look nutzt: BO = Team-Pill (voller
 * Name ohne „Team "-Präfix, eigene Hash-Farbe), FO = Kürzel-/Initialen-Kreis.
 *
 * `FoBoBadge` ist bewusst vollständig INLINE gestylt (keine `.kvp-rd`-Klassen),
 * damit es in jedem CSS-Scope funktioniert — im `.kvp-rd`-Tabellen-Kontext wie
 * in der `.vgd`-Vorgangskarte. Reine Ableitungen liegen in `foBoLogic.ts`.
 */

// Re-Export der reinen Logik, damit Konsumenten nur „shared/foBo" importieren.
export { foBoLabels, hashColor, initials } from "./foBoLogic";
export type { FoBoTeam, FoBoUser } from "./foBoLogic";

export interface FoBoBadgeProps {
    boTeam?: FoBoTeam | null;
    foUser?: FoBoUser | null;
    /**
     * Alle FO (28.09.2026: mehrere FO je Kontakt), primärer zuerst. Ist die
     * Liste gesetzt und nicht leer, zeichnet das Badge je FO einen Kreis
     * nebeneinander; sonst gilt wie bisher `foUser`.
     */
    foUsers?: FoBoUser[] | null;
    /** Farbe für „–"/Trenner (Default = kvp-rd `--ink-3`). */
    muted?: string;
}

/** Ein FO-Kürzel-Kreis (Kürzel, sonst Initialen), Farbe je User-Id. */
export const FoKreis: React.FC<{ foUser: FoBoUser; size?: number }> = ({ foUser, size = 28 }) => {
    const { foLabel } = foBoLabels(null, foUser);
    if (!foLabel) return null;
    const foName = foUser.displayName?.trim() || null;
    const foKuerzel = foUser.kuerzel?.trim() || null;
    const farbe = hashColor(foUser.userId || foKuerzel || foName || foLabel);
    return (
        <span
            title={[foName, foKuerzel].filter(Boolean).join(" · ") || undefined}
            style={{
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
                width: size,
                height: size,
                borderRadius: "50%",
                fontWeight: 800,
                fontSize: size >= 26 ? 10.5 : 9.5,
                letterSpacing: "0.02em",
                background: farbe.bg,
                color: farbe.fg,
            }}
        >
            {foLabel}
        </span>
    );
};

/** Mehrere FO-Kreise nebeneinander (primärer zuerst). */
export const FoKreise: React.FC<{ foUsers: FoBoUser[]; size?: number }> = ({ foUsers, size }) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        {foUsers.map((u, i) => (
            <FoKreis key={u.userId || u.kuerzel || i} foUser={u} size={size} />
        ))}
    </span>
);

/**
 * „BO | FO"-Badge: Team-Pill + FO-Kreis(e), fehlende Seite als „–". Sind BEIDE
 * Seiten leer, rendert die Komponente ein einzelnes „–".
 */
export const FoBoBadge: React.FC<FoBoBadgeProps> = ({ boTeam, foUser, foUsers, muted = "#9a8c95" }) => {
    const alleFo = (foUsers && foUsers.length > 0 ? foUsers : foUser ? [foUser] : []).filter(
        (u) => !!foBoLabels(null, u).foLabel,
    );
    const { boLabel } = foBoLabels(boTeam, null);
    const boName = boTeam?.name?.trim() || null;

    const emptyMark = (
        <span style={{ color: muted, fontWeight: 700, minWidth: 14, textAlign: "center" }}>–</span>
    );

    if (!boLabel && alleFo.length === 0) return emptyMark;

    const boColor = boName ? hashColor(boName) : null;

    return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
            {boLabel && boColor ? (
                <span
                    title={boName ?? undefined}
                    style={{
                        display: "inline-block",
                        maxWidth: 150,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        padding: "3px 10px",
                        borderRadius: 999,
                        fontWeight: 700,
                        fontSize: 11,
                        letterSpacing: "0.01em",
                        background: boColor.bg,
                        color: boColor.fg,
                    }}
                >
                    {boLabel}
                </span>
            ) : (
                emptyMark
            )}
            <span style={{ color: muted, fontWeight: 600 }}>|</span>
            {alleFo.length > 0 ? <FoKreise foUsers={alleFo} /> : emptyMark}
        </span>
    );
};
